import { Icons } from '@/src/modules/components/Icons';
import { cn } from '@/src/infrastructure/lib/utils';
import type { BackendOccasionType, InvitationSortBy, InvitationTab } from '@/src/types';



const OCCASIONS: Array<{ value: '' | BackendOccasionType; label: string }> = [
  { value: '', label: 'All occasions' },
  { value: 'WEDDING', label: 'Wedding' },
  { value: 'SAVE_THE_DATE', label: 'Save the Date' },
  { value: 'BIRTHDAY', label: 'Birthday' },
  { value: 'ANNIVERSARY', label: 'Anniversary' },
  { value: 'BABY_SHOWER', label: 'Baby Shower' },
  { value: 'SEND_OFF', label: 'Send-off' },
  { value: 'ENGAGEMENT', label: 'Engagement' },
  { value: 'CUSTOM', label: 'Custom' },
];

const SORTS: Array<{ value: InvitationSortBy; label: string }> = [
  { value: 'createdAt', label: 'Newest first' },
  { value: 'updatedAt', label: 'Recently updated' },
  { value: 'eventDate', label: 'Event date' },
  { value: 'expiresAt', label: 'Expiry date' },
  { value: 'totalViews', label: 'Most viewed' },
  { value: 'totalRsvps', label: 'Most RSVPs' },
];

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  occasionType: BackendOccasionType | '';
  onOccasionChange: (occasion: BackendOccasionType | '') => void;
  sortBy: InvitationSortBy;
  onSortByChange: (sort: InvitationSortBy) => void;
  sortOrder: 'asc' | 'desc';
  onSortOrderChange: (order: 'asc' | 'desc') => void;
  onReset: () => void;
}

/**
 * Header strip: tabs (All/Active/Inactive/Drafts/Archived), debounced search,
 * occasion filter, sort dropdown. Sort order is a separate toggle so we don't
 * have to duplicate every sortable column with an asc/desc variant.
 */
export function InvitationFilters(props: Props) {
  return (
    <div className="space-y-4 mb-6">
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Icons.Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant"
          />
          <input
            type="text"
            value={props.search}
            onChange={(e) => props.onSearchChange(e.target.value)}
            placeholder="Search by event or host name…"
            className="w-full rounded-xl border border-outline-variant/40 bg-surface pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        <select
          value={props.occasionType}
          onChange={(e) =>
            props.onOccasionChange((e.target.value as BackendOccasionType) || '')
          }
          className="rounded-xl border border-outline-variant/40 bg-surface px-3 py-2.5 text-sm"
        >
          {OCCASIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          value={props.sortBy}
          onChange={(e) => props.onSortByChange(e.target.value as InvitationSortBy)}
          className="rounded-xl border border-outline-variant/40 bg-surface px-3 py-2.5 text-sm"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => props.onSortOrderChange(props.sortOrder === 'asc' ? 'desc' : 'asc')}
          className="rounded-xl border border-outline-variant/40 bg-surface px-3 py-2.5 text-sm font-medium"
          title={`Sorting ${props.sortOrder === 'asc' ? 'ascending' : 'descending'}`}
        >
          {props.sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
        </button>

        <button
          type="button"
          onClick={props.onReset}
          className="rounded-xl bg-surface-low px-3 py-2.5 text-sm font-medium text-on-surface-variant hover:bg-outline-variant/30"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
